import api from '../config/axios';
import {
  ExportRequest,
  ExportResponse,
  ExportFieldsResponse,
  PresetResponse,
  SinglePresetResponse,
  CreatePresetRequest,
  UpdatePresetRequest,
  DuplicatePresetRequest,
  ExportPreset
} from '../types/export';

class ExportService {
  /**
   * Export invoices to Excel/CSV
   * @param exportRequest - Export configuration
   * @returns Promise<void> - File download
   */
  async exportInvoices(exportRequest: ExportRequest): Promise<void> {
    try {
      const response = await api.post('/export/invoices', exportRequest, {
        responseType: 'blob'
      });

      // Create download link
      const blob = new Blob([response.data], { 
        type: response.headers['content-type'] || 'application/octet-stream' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Get filename from response headers
      const contentDisposition = response.headers['content-disposition'];
      let filename = exportRequest.fileName || 'invoices';
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      // Add extension if not present
      if (!filename.includes('.')) {
        filename += exportRequest.exportFormat === 'excel' ? '.xlsx' : '.csv';
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Export error:', error);
      throw new Error('Export failed');
    }
  }

  /**
   * Get available export fields
   * @returns Promise<ExportFieldsResponse>
   */
  async getExportFields(): Promise<ExportFieldsResponse> {
    try {
      const response = await api.get<ExportFieldsResponse>('/export/fields');
      return response.data;
    } catch (error) {
      console.error('Get export fields error:', error);
      throw new Error('Failed to retrieve export fields');
    }
  }

  /**
   * Get filter templates and business units
   * @returns Promise<any>
   */
  async getFilterTemplates(): Promise<any> {
    try {
      const response = await api.get('/export/filter-templates');
      return response.data;
    } catch (error) {
      console.error('Get filter templates error:', error);
      throw new Error('Failed to retrieve filter templates');
    }
  }

  /**
   * Get all export presets for current user
   * @returns Promise<PresetResponse>
   */
  async getExportPresets(): Promise<PresetResponse> {
    try {
      const response = await api.get<PresetResponse>('/export/presets');
      return response.data;
    } catch (error) {
      console.error('Get export presets error:', error);
      throw new Error('Failed to retrieve export presets');
    }
  }

  /**
   * Get export preset by ID
   * @param id - Preset ID
   * @returns Promise<SinglePresetResponse>
   */
  async getExportPreset(id: number): Promise<SinglePresetResponse> {
    try {
      const response = await api.get<SinglePresetResponse>(`/export/presets/${id}`);
      return response.data;
    } catch (error) {
      console.error('Get export preset error:', error);
      throw new Error('Failed to retrieve export preset');
    }
  }

  /**
   * Create new export preset
   * @param presetData - Preset data
   * @returns Promise<ExportResponse>
   */
  async createExportPreset(presetData: CreatePresetRequest): Promise<ExportResponse> {
    try {
      const response = await api.post<ExportResponse>('/export/presets', presetData);
      return response.data;
    } catch (error) {
      console.error('Create export preset error:', error);
      throw new Error('Failed to create export preset');
    }
  }

  /**
   * Update existing export preset
   * @param id - Preset ID
   * @param presetData - Updated preset data
   * @returns Promise<ExportResponse>
   */
  async updateExportPreset(id: number, presetData: UpdatePresetRequest): Promise<ExportResponse> {
    try {
      const response = await api.put<ExportResponse>(`/export/presets/${id}`, presetData);
      return response.data;
    } catch (error) {
      console.error('Update export preset error:', error);
      throw new Error('Failed to update export preset');
    }
  }

  /**
   * Delete export preset
   * @param id - Preset ID
   * @returns Promise<{ message: string }>
   */
  async deleteExportPreset(id: number): Promise<{ message: string }> {
    try {
      const response = await api.delete<{ message: string }>(`/export/presets/${id}`);
      return response.data;
    } catch (error) {
      console.error('Delete export preset error:', error);
      throw new Error('Failed to delete export preset');
    }
  }

  /**
   * Set preset as default
   * @param id - Preset ID
   * @returns Promise<ExportResponse>
   */
  async setDefaultPreset(id: number): Promise<ExportResponse> {
    try {
      const response = await api.post<ExportResponse>(`/export/presets/${id}/set-default`);
      return response.data;
    } catch (error) {
      console.error('Set default preset error:', error);
      throw new Error('Failed to set default preset');
    }
  }

  /**
   * Duplicate export preset
   * @param id - Preset ID
   * @param newName - New name for duplicated preset
   * @returns Promise<ExportResponse>
   */
  async duplicateExportPreset(id: number, newName: string): Promise<ExportResponse> {
    try {
      const response = await api.post<ExportResponse>(`/export/presets/${id}/duplicate`, {
        newName
      });
      return response.data;
    } catch (error) {
      console.error('Duplicate export preset error:', error);
      throw new Error('Failed to duplicate export preset');
    }
  }

  /**
   * Quick export using default preset
   * @param filters - Optional filters to apply
   * @returns Promise<void>
   */
  async quickExport(filters?: any): Promise<void> {
    try {
      // Get user's default preset
      const presetsResponse = await this.getExportPresets();
      const defaultPreset = presetsResponse.presets.find(p => p.isDefault);
      
      if (!defaultPreset) {
        throw new Error('No default export preset found. Please create one first.');
      }

      // Use default preset configuration
      const exportRequest: ExportRequest = {
        filters: filters || defaultPreset.filters,
        selectedFields: defaultPreset.selectedFields,
        exportFormat: defaultPreset.exportFormat,
        fileName: defaultPreset.name,
        presetId: defaultPreset.id
      };

      await this.exportInvoices(exportRequest);
    } catch (error) {
      console.error('Quick export error:', error);
      throw error;
    }
  }

  /**
   * Export selected invoices
   * @param invoiceIds - Array of invoice IDs to export
   * @param exportRequest - Export configuration
   * @returns Promise<void>
   */
  async exportSelectedInvoices(invoiceIds: number[], exportRequest: ExportRequest): Promise<void> {
    try {
      // Add invoice ID filter to export request
      const requestWithIds = {
        ...exportRequest,
        filters: {
          ...exportRequest.filters,
          invoiceIds // This will be handled by the backend
        }
      };

      await this.exportInvoices(requestWithIds);
    } catch (error) {
      console.error('Export selected invoices error:', error);
      throw error;
    }
  }
}

export default new ExportService();
